import React, { useState, useMemo, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { useQuery } from 'react-query';
import MuiTypography from '@material-ui/core/Typography';
import MuiPaper from '@material-ui/core/Paper';
import MuiFormControl from '@material-ui/core/FormControl';
import MuiSelect from '@material-ui/core/Select';
import MuiMenuItem from '@material-ui/core/MenuItem';

import MuiGrid from '@material-ui/core/Grid';
import MuiButton from '@material-ui/core/Button';
import MuiAddIcon from '@material-ui/icons/Add';
import MuiSearchIcon from '@material-ui/icons/Search';

import ViewWrapper from 'views/ViewWrapper';
import TablePayees from 'components/TablePayees/table-payees';
import { detereminePaymentAccountStatus } from 'components/TablePayees/ACCOUNT_STATUS';
import DialogCreatePayee from 'components/DialogCreatePayee/dialog-create-payee';
import MuiTextField from '@material-ui/core/TextField';
import MuiInputAdornment from '@material-ui/core/InputAdornment';
import debounce from 'lodash/debounce';
import MuiInputLabel from '@material-ui/core/InputLabel';

import TYPE from 'components/TablePayees/PAYEE_TYPE.json';

import * as API from 'api';
const Groups = API.Groups();
const Payees = API.Payees();

const styles = theme => ({
  paper: {
    overflow: 'hidden',
    padding: theme.spacing(2),
  },
  select: {
    minWidth: '100px',
  },
  title: {
    paddingBottom: theme.spacing(2),
  },
  actionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(1),
  },
  actionButton: {
    marginRight: theme.spacing(1),
  },
  formRow: {
    display: 'flex',
  },
});

const createRowData = row => {
  return {
    id: row.id,
    email: row?.payment_account?.user?.email || '—',
    hasSplits: Number(row.splitsCount) > 0,
    full_name:
      row?.full_name ||
      (row?.payment_account?.user ? row?.payment_account?.user?.name : row?.payment_account?.group?.name),
    type: row?.payment_account?.user ? TYPE.USER : TYPE.GROUP,
    stripe_account_id: row?.payment_account?.stripe_account_id,
    group_name: row?.userGroup?.name,
    payment_account_status: detereminePaymentAccountStatus(row?.payment_account),
  };
};

const _Payees = ({ ...props }) => {
  const { classes } = props;

  const [selectedGroupId, setSelectedGroupId] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSearchTerm, setSelectedSearchTerm] = useState('');
  const [isPayeeDialogOpen, setIsPayeeDialogOpen] = useState(false);

  const {
    data: searchedPayees,
    isLoading: isLoadingSearchPayees,
    refetch: refetchSearchPayees,
  } = useQuery(['payees-search', selectedSearchTerm], () => Payees.search(selectedSearchTerm), {
    select: data => data.map(d => createRowData(d)),
    enabled: !!selectedSearchTerm,
  });

  const { data: groups, isLoading: isLoadingGroups } = useQuery(['groups'], () => Groups.getAllSegmented());

  const {
    data: groupPayees,
    refetch,
    isLoading: isLoadingPayees,
  } = useQuery(['payees', selectedGroupId], () => Groups.getPayees(selectedGroupId), {
    select: data => data.map(d => createRowData(d)),
    enabled: !!selectedGroupId,
  });

  const debouncedSearch = useMemo(
    () =>
      debounce(value => {
        setSelectedSearchTerm(value);
        setSelectedGroupId('');
      }, 350),
    []
  );

  const handleSearch = ({ target: { value } }) => {
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleGroupChange = ({ target: { value } }) => {
    setSelectedGroupId(value);
    setSearchTerm('');
    setSelectedSearchTerm('');
  };

  useEffect(() => {
    const searchParam = new URLSearchParams(window.location.search).get('search');
    if (searchParam) {
      setSearchTerm(searchParam);
      debouncedSearch(searchParam);
    }
  }, []);

  return (
    <ViewWrapper>
      <MuiGrid item xs={12}>
        <MuiPaper className={classes.paper}>
          <MuiTypography className={classes.title} variant="h5">
            Payees
          </MuiTypography>
          <div className={classes.actionRow}>
            <MuiFormControl>
              <MuiInputLabel>Group</MuiInputLabel>
              <MuiSelect
                disabled={isLoadingGroups}
                className={classes.select}
                value={selectedGroupId}
                onChange={handleGroupChange}>
                {groups &&
                  groups.map((group, i) => (
                    <MuiMenuItem key={i} value={group.id}>
                      {group.name}
                    </MuiMenuItem>
                  ))}
              </MuiSelect>
            </MuiFormControl>
            <MuiTextField
              variant="outlined"
              label="Search"
              placeholder="Email Address or Name"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <MuiInputAdornment position="start">
                    <MuiSearchIcon />
                  </MuiInputAdornment>
                ),
              }}
            />
            <MuiButton
              variant="contained"
              size="small"
              className={classes.actionButton}
              startIcon={<MuiAddIcon />}
              onClick={() => setIsPayeeDialogOpen(true)}>
              Add Payee
            </MuiButton>
          </div>
          {(!!selectedGroupId || !!selectedSearchTerm) && (
            <TablePayees
              groupId={selectedGroupId}
              searchTerm={selectedSearchTerm}
              refetch={selectedSearchTerm ? refetchSearchPayees : refetch}
              payees={selectedSearchTerm ? searchedPayees : groupPayees}
              isLoading={isLoadingPayees || isLoadingSearchPayees}
            />
          )}
        </MuiPaper>
      </MuiGrid>
      <DialogCreatePayee
        open={isPayeeDialogOpen}
        groups={groups}
        onClose={() => setIsPayeeDialogOpen(false)}
        onCreated={refetch}
        groupId={selectedGroupId}
      />
    </ViewWrapper>
  );
};

export default withStyles(styles)(_Payees);
